<template>
  <v-container grid-list-md fluid>
    <!-- Alert informativo para usuários não admin em condomínio -->
    <v-alert
      v-if="isCondominioMode && !isAdminAccess"
      type="warning"
      :value="true"
      class="mb-3"
    >
      <strong>Atenção:</strong> Apenas administradores podem adicionar, editar ou excluir registros de Rendimentos Tributáveis Recebidos de PJ em condomínios.
    </v-alert>

    <v-layout wrap justify-space-between>
      <v-flex xs12 sm2>
        <v-btn
          color="primary"
          @click="create"
          class="mb-4"
          :disabled="!hasCondominioAccess(entity, 'create') || (isCondominioMode && !isAdminAccess)"
        >
          {{ this.translate("system.button.add.title") }}
          <v-icon dark>{{ this.translate("system.button.add.icon") }}</v-icon>
        </v-btn>
      </v-flex>
      <v-flex xs12 sm4>
        <v-text-field
          append-icon="search"
          label="Pesquisar"
          v-model="search"
          @change="updateSum"
        ></v-text-field>
      </v-flex>
      <v-flex xs12 sm1>
        <v-menu offset-y>
          <v-btn slot="activator" icon class="ml-2" title="Relatórios">
            <v-icon>description</v-icon>
          </v-btn>
          <v-list>
            <!-- PDF -->
            <v-list-tile @click="exportReport(1)">
              <v-list-tile-action>
                <v-icon color="red darken-1">picture_as_pdf</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Relatório em PDF</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <!-- Excel -->
            <v-list-tile @click="exportReport(2)">
              <v-list-tile-action>
                <v-icon color="green darken-2">table_chart</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>Relatório em Excel</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-flex>

      <!--Index-->
      <v-flex xs12>
        <v-data-table
          ref="RendimentoRecebidoPJTable"
          :headers="headers"
          :items="items"
          :search="search"
          class="elevation-1"
          :rows-per-page-items="[100, 50, 25, { text: 'Todos', value: -1 }]"
          rows-per-page-text="Registros por página"
          no-data-text="Não existem registros"
          no-results-text="Nenhum registro encontrado"
        >
          <template slot="items" slot-scope="props">
            <td>{{ props.item.sequencia_ir }}</td>
            <td>{{ props.item.id_texto }}</td>
            <td>{{ props.item.cpf_cnpj | formatCpfCnpj }}</td>
            <td align="right">
              {{ props.item.total_recebido_pj | formatCurrency }}
            </td>
            <td align="right">
              {{ props.item.total_contribuicao_previdenciaria | formatCurrency }}
            </td>
            <td align="right">
              {{ props.item.total_imposto_retido | formatCurrency }}
            </td>
            <td align="right">
              {{ props.item.total_salary_13 | formatCurrency }}
            </td>
            <td align="right">
              {{ props.item.total_irrf_salary_13 | formatCurrency }}
            </td>
            <td v-if="temDependente">
              {{ props.item.dependente ? props.item.dependente.id_texto : "" }}
            </td>
            <td align="center" style="min-width: 150px">
              <v-btn
                icon
                class="mx-0"
                @click="editItem(props.item)"
                :disabled="!hasCondominioAccess(entity, 'update') || (isCondominioMode && !isAdminAccess)"
              >
                <v-icon color="teal">edit</v-icon>
              </v-btn>
              <v-btn
                icon
                class="mx-0"
                @click="deleteItem(props.item)"
                :disabled="!hasCondominioAccess(entity, 'delete') || (isCondominioMode && !isAdminAccess)"
              >
                <v-icon color="pink">delete</v-icon>
              </v-btn>
            </td>
          </template>
          <template v-slot:footer>
            <td colspan="3">
              <strong>Total Geral</strong>
            </td>
            <td align="right">
              <strong>{{ total.total_recebido_pj | formatCurrency }}</strong>
            </td>
            <td align="right">
              <strong>
                {{ total.total_contribuicao_previdenciaria | formatCurrency }}
              </strong>
            </td>
            <td align="right">
              <strong>{{ total.total_imposto_retido | formatCurrency }}</strong>
            </td>
            <td align="right">
              <strong>{{ total.total_salary_13 | formatCurrency }}</strong>
            </td>
            <td align="right">
              <strong>{{ total.total_irrf_salary_13 | formatCurrency }}</strong>
            </td>
          </template>
          <template slot="pageText" slot-scope="props">
            {{ props.pageStart }} - {{ props.pageStop }} de
            {{ props.itemsLength }}
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <!--Create Edit-->
    <v-dialog v-model="dialog" max-width="1600px" persistent>
      <v-card>
        <v-card-text>
          <v-container grid-list-md fluid>
            <v-layout wrap justify-right align-right>
              <v-flex xs12>
                <span class="display-1">{{ formTitle }}</span>
              </v-flex>
              <v-flex xs12 md12>
                <v-text-field
                  name="id_texto"
                  v-model="editedItem.id_texto"
                  data-vv-validate-on="blur"
                  v-validate="'required'"
                  :error-messages="errors.collect('id_texto')"
                  :data-vv-as="translate('table.' + entity + '.field.id_texto')"
                  :label="translate('table.' + entity + '.field.id_texto')"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md6>
                <v-text-field
                  name="sequencia_ir"
                  data-vv-validate-on="blur"
                  v-model="editedItem.sequencia_ir"
                  counter
                  maxlength="19"
                  v-validate="'required'"
                  @blur="checkDuplicate(items, 'sequencia_ir', editedItem.sequencia_ir)"
                  :error-messages="errors.collect('sequencia_ir')"
                  :data-vv-as="translate('table.' + entity + '.field.sequencia_ir')"
                  :label="translate('table.' + entity + '.field.sequencia_ir')"
                ></v-text-field>
                <div
                  style="color: #ff5252; font-size: 12px !important; position: relative; margin-top: -20px"
                  v-if="sequencia_ir_duplicated"
                >
                  Este valor já foi inserido anteriormente.
                </div>
              </v-flex>
              <v-flex xs12 md6>
                <p-cpf-cnpj
                  name="cpf_cnpj"
                  v-model="editedItem.cpf_cnpj"
                  v_validate="required"
                ></p-cpf-cnpj>
              </v-flex>
              <v-flex xs12 v-if="temDependente">
                <v-autocomplete
                  :label="translate('table.' + entity + '.field.pessoa_dependente_id')"
                  v-model="editedItem.pessoa_dependente_id"
                  :items="dependente_items"
                  item-text="id_texto"
                  item-value="id"
                ></v-autocomplete>
              </v-flex>

              <!--INICIO------------------- Distribuição ------------------->
              <v-flex xs12 md8>
                <span class="headline">Distribuições</span>
              </v-flex>
              <v-flex xs12 md4 class="text-xs-right">
                <v-btn color="primary" @click="createSubItem()" class="my-1">
                  {{ this.translate("system.button.add.title") }}
                  <v-icon dark>
                    {{ this.translate("system.button.add.icon") }}
                  </v-icon>
                </v-btn>
              </v-flex>
              <v-flex xs12>
                <v-data-table
                  :headers="sub_headers"
                  :items="editedItem.sub_items"
                  id="sub_items_table"
                  hide-actions
                  no-data-text="Não existem registros"
                  no-results-text="Nenhum registro encontrado"
                >
                  <template slot="items" slot-scope="props">
                    <td>
                      <p-date limitPreviousYears v-model="props.item.data"></p-date>
                    </td>
                    <td>
                      <p-money
                        name="recebido_pj"
                        v-model="props.item.recebido_pj"
                        :v_validate="'positiveNumber'"
                        data_vv_as="Rendimento Recebido de PJ"
                      ></p-money>
                    </td>
                    <td>
                      <p-money
                        name="contribuicao_previdenciaria"
                        v-model="props.item.contribuicao_previdenciaria"
                        :v_validate="'positiveNumber'"
                        data_vv_as="Contribuição Previdenciária"
                      ></p-money>
                    </td>
                    <td>
                      <p-money
                        name="imposto_retido"
                        v-model="props.item.imposto_retido"
                        :v_validate="'positiveNumber'"
                        data_vv_as="Imposto Retido na Fonte"
                      ></p-money>
                    </td>
                    <td>
                      <p-money
                        name="salary_13"
                        v-model="props.item.salary_13"
                        :v_validate="'positiveNumber'"
                        data_vv_as="13º Salário"
                      ></p-money>
                    </td>
                    <td>
                      <p-money
                        name="irrf_salary_13"
                        v-model="props.item.irrf_salary_13"
                        :v_validate="'positiveNumber'"
                        data_vv_as="IRRF 13º Salário"
                      ></p-money>
                    </td>
                    <td align="center">
                      <v-btn icon class="mx-0" @click="deleteSubItem(props.item)">
                        <v-icon color="pink">delete</v-icon>
                      </v-btn>
                    </td>
                  </template>
                </v-data-table>
              </v-flex>

              <v-flex md2></v-flex>
              <v-flex xs12 md2>
                <p-money
                  disabled
                  :label="translate('table.' + entity + '.field.recebido_pj')"
                  :value="editedItemSum.recebido_pj"
                ></p-money>
              </v-flex>
              <v-flex xs12 md2>
                <p-money
                  disabled
                  :label="translate('table.' + entity + '.field.contribuicao_previdenciaria')"
                  :value="editedItemSum.contribuicao_previdenciaria"
                ></p-money>
              </v-flex>
              <v-flex xs12 md2>
                <p-money
                  disabled
                  :label="translate('table.' + entity + '.field.imposto_retido')"
                  :value="editedItemSum.imposto_retido"
                ></p-money>
              </v-flex>
              <v-flex xs12 md2>
                <p-money
                  disabled
                  :label="translate('table.' + entity + '.field.salary_13')"
                  :value="editedItemSum.salary_13"
                ></p-money>
              </v-flex>
              <v-flex xs12 md2>
                <p-money
                  disabled
                  :label="translate('table.' + entity + '.field.irrf_salary_13')"
                  :value="editedItemSum.irrf_salary_13"
                ></p-money>
              </v-flex>
              <!--FIM------------------- Distribuição ------------------->

              <v-flex xs12>
                <v-btn class="mx-1" color="primary darken-2" :disabled="saving" :loading="saving" @click.native="save()">Salvar</v-btn>
                <v-btn class="mx-1" @click.native="close(400)">Cancelar</v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmação para admin/dev em condomínio -->
    <v-dialog v-model="confirmDialogCondominio" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">Confirmação de Uso</span>
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" :value="true" class="mb-3">
            Você está acessando a aba de <strong>Rendimentos Tributáveis Recebidos de PJ</strong> em um <strong>Condomínio</strong>.
          </v-alert>
          <p>
            Esta funcionalidade é normalmente utilizada apenas para <strong>Contribuintes (Particulares)</strong>.
          </p>
          <p>
            Deseja realmente continuar utilizando esta aba para o condomínio?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat @click="confirmDialogCondominio = false">
            Sim, continuar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import crud from "./../../mixin/crud";
import PDate from "../shared/PDate";
import PMoney from "../shared/PMoney";
import Helper from "./../../classes/helper";
import acl from "./../../mixin/acl";
import PCpfCnpj from "../shared/PCpfCnpj";

export default {
  components: {
    PDate,
    PMoney,
    PCpfCnpj,
  },
  mixins: [crud, acl],
  data: () => ({
    entity: "condominio_rendimentorecebidopj",
    items: [],
    total: {
      total_recebido_pj: 0,
      total_contribuicao_previdenciaria: 0,
      total_imposto_retido: 0,
      total_salary_13: 0,
      total_irrf_salary_13: 0,
    },
    sequencia_ir_duplicated: false,
    confirmDialogCondominio: false,
  }),
  name: "RendimentoRecebidoPj",
  props: ["condominio_id", "values", "initials", "temDependente", "dependente_items", "particular"],
  created() {
    this.items = this.values;
    this.editedItem = {
      condominio_id: this.condominio_id,
      ativo: true,
      sub_items: [],
      pessoa_dependente_id: null,
    };
    this.defaultItem = {
      condominio_id: this.condominio_id,
      ativo: true,
      sub_items: [],
      pessoa_dependente_id: null,
    };
  },
  watch: {
    items(newValue, oldValue) {
      this.updateSum();
    },
  },
  mounted() {
    // Verifica se está em modo condomínio e se o usuário é admin/dev
    if (this.isCondominioMode && this.isAdminAccess) {
      this.confirmDialogCondominio = true;
    }
  },
  computed: {
    // Verifica se está em modo condomínio (particular = false)
    isCondominioMode() {
      return this.particular === false;
    },
    // Verifica se o usuário tem acesso de administrador
    isAdminAccess() {
      const acessoInterno = this.$auth.user().acesso_interno;
      return acessoInterno === 'dev' || acessoInterno === 'admin';
    },
    headers: function () {
      if (this.temDependente) {
        return [
          {
            text: this.translate("table." + this.entity + ".field.sequencia_ir"),
            value: "sequencia_ir",
            width: "10%",
          },
          {
            text: this.translate("table." + this.entity + ".field.id_texto"),
            value: "id_texto",
            width: "30%",
          },
          {
            text: this.translate("table." + this.entity + ".field.cpf_cnpj"),
            value: "cpf_cnpj",
            width: "30%",
          },
          {
            text: this.translate("table." + this.entity + "_item.field.recebido_pj"),
            sortable: false,
            value: "recebido_pj",
          },
          {
            text: this.translate(
              "table." + this.entity + "_item.field.contribuicao_previdenciaria"
            ),
            sortable: false,
            value: "contribuicao_previdenciaria",
          },
          {
            text: this.translate("table." + this.entity + "_item.field.imposto_retido"),
            sortable: false,
            value: "imposto_retido",
          },
          {
            text: this.translate("table." + this.entity + "_item.field.salary_13"),
            sortable: false,
            value: "salary_13",
          },
          {
            text: this.translate("table." + this.entity + "_item.field.irrf_salary_13"),
            sortable: false,
            value: "irrf_salary_13",
          },
          {
            text: this.translate("table." + this.entity + ".field.pessoa_dependente_id"),
            value: "dependente.id_texto",
            width: "20%",
          },
          {
            text: this.translate("system.table.action"),
            value: "action",
            sortable: false,
            align: "center",
            width: "10%",
          },
        ];
      } else {
        return [
          {
            text: this.translate("table." + this.entity + ".field.sequencia_ir"),
            value: "sequencia_ir",
            width: "10%",
          },
          {
            text: this.translate("table." + this.entity + ".field.id_texto"),
            value: "id_texto",
            width: "50%",
          },
          {
            text: this.translate("table." + this.entity + ".field.cpf_cnpj"),
            value: "cpf_cnpj",
            width: "30%",
          },
          {
            text: this.translate("table." + this.entity + "_item.field.recebido_pj"),
            sortable: false,
            value: "recebido_pj",
          },
          {
            text: this.translate(
              "table." + this.entity + "_item.field.contribuicao_previdenciaria"
            ),
            sortable: false,
            value: "contribuicao_previdenciaria",
          },
          {
            text: this.translate("table." + this.entity + "_item.field.imposto_retido"),
            sortable: false,
            value: "imposto_retido",
          },
          {
            text: this.translate("table." + this.entity + "_item.field.salary_13"),
            sortable: false,
            value: "salary_13",
          },
          {
            text: this.translate("table." + this.entity + "_item.field.irrf_salary_13"),
            sortable: false,
            value: "irrf_salary_13",
          },
          {
            text: this.translate("system.table.action"),
            value: "action",
            sortable: false,
            align: "center",
            width: "10%",
          },
        ];
      }
    },
    sub_headers: function () {
      return [
        {
          text: this.translate("table." + this.entity + "_item.field.data"),
          sortable: false,
          value: "data",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.recebido_pj"),
          sortable: false,
          value: "recebido_pj",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.contribuicao_previdenciaria"),
          sortable: false,
          value: "contribuicao_previdenciaria",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.imposto_retido"),
          sortable: false,
          value: "imposto_retido",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.salary_13"),
          sortable: false,
          value: "salary_13",
        },
        {
          text: this.translate("table." + this.entity + "_item.field.irrf_salary_13"),
          sortable: false,
          value: "irrf_salary_13",
        },
        {
          text: this.translate("system.table.action"),
          value: "action",
          sortable: false,
          align: "center",
        },
      ];
    },
    editedItemSum() {
      return this.editedItem.sub_items.reduce(
        (acc, cur) => {
          acc["recebido_pj"] += cur["recebido_pj"];
          acc["contribuicao_previdenciaria"] += cur["contribuicao_previdenciaria"];
          acc["imposto_retido"] += cur["imposto_retido"];
          acc["salary_13"] += cur["salary_13"];
          acc["irrf_salary_13"] += cur["irrf_salary_13"];
          return acc;
        },
        {
          recebido_pj: 0,
          contribuicao_previdenciaria: 0,
          imposto_retido: 0,
          salary_13: 0,
          irrf_salary_13: 0,
        }
      );
    }
  },
  methods: {
    beforeSave() {
      // Verifica se todas as variáveis estão zeradas em pelo menos um objeto do array
      let allZeros = this.editedItem.sub_items.some((item) => {
        return (
          item.imposto_retido == 0 &&
          item.irrf_salary_13 == 0 &&
          item.recebido_pj == 0 &&
          item.salary_13 == 0
        );
      });

      // Se todas as variáveis estiverem zeradas em pelo menos um objeto, mostra a snackbar e interrompe a execução
      if (allZeros) {
        this.$store.commit("snackbar/change_values", {
          text: "Por favor, verifique as Distribuições. Pelo menos um destes campos precisa ser preenchido: Rendimento Recebido de PJ, Contribuição Previdenciária, Imposto Retido na Fonte, 13º Salário e IRRF 13º Salário.",
          color: "error",
          dialog: true,
          timeout: 5000,
        });

        this.$store.commit("loader/change_dialog", false);
        throw new Error("Todos os campos estão zerados em pelo menos um objeto.");
      }
    },
    checkDuplicate(items, index, value) {
      // this.sequencia_ir_duplicated = items.find((item) => {
      //   return item[index] === value;
      // });
      // if (this.sequencia_ir_duplicated) {
      //   event.preventDefault();
      //   event.target.select();
      // }
    },
    beforeCreate() {
      this.editedItem.sequencia_ir = Helper.generateSequenciaIr(this.initials, "RPJ", this.values);
    },
    createSubItem: function () {
      this.editedItem.sub_items.push({
        data: "",
        recebido_pj: 0,
        contribuicao_previdenciaria: 0,
        imposto_retido: 0,
        salary_13: 0,
        irrf_salary_13: 0,
      });
    },
    deleteSubItem(item) {
      const index = this.editedItem.sub_items.indexOf(item);
      this.editedItem.sub_items.splice(index, 1);
    },
    updateSum() {
      this.total = this.$refs.RendimentoRecebidoPJTable.filteredItems.reduce(
        (acc, item) => {
          const sum = item.sub_items.reduce(
            (acc2, sub_item) => {
              acc2.total_recebido_pj += sub_item.recebido_pj;
              acc2.total_contribuicao_previdenciaria += sub_item.contribuicao_previdenciaria;
              acc2.total_imposto_retido += sub_item.imposto_retido;
              acc2.total_salary_13 += sub_item.salary_13;
              acc2.total_irrf_salary_13 += sub_item.irrf_salary_13;
              return acc2;
            },
            {
              total_recebido_pj: 0,
              total_contribuicao_previdenciaria: 0,
              total_imposto_retido: 0,
              total_salary_13: 0,
              total_irrf_salary_13: 0,
            }
          );
          acc.total_recebido_pj += sum.total_recebido_pj;
          acc.total_contribuicao_previdenciaria += sum.total_contribuicao_previdenciaria;
          acc.total_imposto_retido += sum.total_imposto_retido;
          acc.total_salary_13 += sum.total_salary_13;
          acc.total_irrf_salary_13 += sum.total_irrf_salary_13;

          item.total_recebido_pj = sum.total_recebido_pj;
          item.total_contribuicao_previdenciaria = sum.total_contribuicao_previdenciaria;
          item.total_imposto_retido = sum.total_imposto_retido;
          item.total_salary_13 = sum.total_salary_13;
          item.total_irrf_salary_13 = sum.total_irrf_salary_13;

          return acc;
        },
        {
          total_recebido_pj: 0,
          total_contribuicao_previdenciaria: 0,
          total_imposto_retido: 0,
          total_salary_13: 0,
          total_irrf_salary_13: 0,
        }
      );

      console.log(this.total);
    },
    // type 1 = pdf 2 = excel
    exportReport (type) {
      this.$store.commit("loader/change_dialog", true);
      this.$http.put(`report/report_details/${this.condominio_id}`, {"type": type, "table": "condominio_rendimentorecebidopj", "search": this.search, "apenasAtivos": this.apenasAtivos}).then(
        (res) => {
          let fileName = res.data;
          window.open(process.env.API + "reports/" + fileName, "_blank");
          this.$store.commit("loader/change_dialog", false);
          setTimeout(function () {
            _this.$http.delete(`report/delete_upload_report/${fileName}`)
          }, 5000)
        },
        (error) => {
          this.$store.commit("loader/change_dialog", false);
          this.$store.commit("snackbar/change_values", {
            text:
              error.body.message ||
              "Desculpe, não conseguimos gerar o relatório no momento. Por favor, tente novamente mais tarde.",
            color: "error",
            dialog: true,
          });
        }
      );
    }
  }
};
</script>

<style scoped>
#sub_items_table {
  border-radius: 4px;
  border: 1px solid #bbb;
  margin-bottom: 4px;
}

#sub_items_table .custom-height {
  padding-top: 8px !important;
}

td .custom-height {
  padding-top: 6px;
  display: flex;
  align-items: center;
}

#proccess {
  position: absolute;
}

#proccess .v-icon {
  position: relative;
  bottom: 50px;
  left: 153px;
}
</style>



// WEBPACK FOOTER //
// src/components/details/RendimentoRecebidoPj.vue